import React, { useMemo } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useHwcgActivationLog } from 'hooks/useHwcgActivationLog';
import PositionActivationChecklist from './PositionActivationChecklist';
import PositionPhaseTasksPanel from './PositionPhaseTasksPanel';
import { HwcgActivationSummary } from './activationLog/HwcgActivationSummary';
import { HwcgActivationUserTimeline } from './activationLog/HwcgActivationUserTimeline';
import {
  summarizeActivationUsers,
  usersByGuids,
  usersForPositions,
  type HwcgActivationUserLog,
} from './activationLog/hwcgActivationLogModel';

type PositionPhaseTabsProps = {
  /** The modal's existing body, which becomes the Team tab. */
  team: React.ReactNode;
  /** Active plus draft occupants of the seat. */
  occupantIds: ReadonlySet<string>;
  positionId?: string;
  positionName?: string;
  incidentId?: string | null;
  /** Opens on Planning when the product tour is showing that seat. */
  initialTab?: 'team' | 'planning';
  /** The incident is still being created, so only Team has anything to show. */
  pendingIncident?: boolean;
};

const PendingIncidentNote: React.FC<{ what: string }> = ({ what }) => (
  <p className="tw:m-0 tw:rounded-md tw:border tw:border-dashed tw:border-border tw:p-4 tw:text-sm tw:text-muted-foreground">
    {what} fills in once the incident is created.
  </p>
);

/**
 * The seat's slice of the incident activation log: who holds (or was noticed
 * for) this position, how far through the two-step notice each is, and the
 * per-person send/reply history.
 */
const PositionActivationLog: React.FC<{
  occupantIds: ReadonlySet<string>;
  positionId?: string;
  incidentId?: string | null;
}> = ({ occupantIds, positionId, incidentId }) => {
  const { data, isLoading, isError } = useHwcgActivationLog(incidentId);

  const users = useMemo<HwcgActivationUserLog[]>(() => {
    if (!data) return [];
    const byPosition = positionId
      ? usersForPositions(data.users, new Set([positionId]))
      : [];
    const byOccupant = usersByGuids(data.users, occupantIds);
    const seen = new Set<string>();
    return [...byOccupant, ...byPosition].filter((user) => {
      if (seen.has(user.userGuid)) return false;
      seen.add(user.userGuid);
      return true;
    });
  }, [data, positionId, occupantIds]);

  const summary = useMemo(() => summarizeActivationUsers(users), [users]);

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Loading activation notices…
      </p>
    );
  }
  if (isError) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-destructive">
        Could not load activation notices.
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className="tw:space-y-4" data-tour="hwcg-position-activation-log">
      <HwcgActivationSummary
        summary={summary}
        title="Activation notices for this seat"
        compact
      />
      {users.length === 0 ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          No one seated here has been sent an activation notice yet. Notices go
          out when a member is assigned to this position.
        </p>
      ) : (
        <div className="tw:space-y-3">
          {users.map((user) => (
            <div
              key={user.userGuid}
              className="tw:rounded-md tw:border tw:border-border tw:p-3"
            >
              <HwcgActivationUserTimeline user={user} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The HWCG layout of the roster position modal: the seat viewed by incident
 * phase rather than as one long form.
 *
 * Team is first and holds everything the modal showed before the flag, so
 * nothing moves for anyone who is used to it.
 */
const PositionPhaseTabs: React.FC<PositionPhaseTabsProps> = ({
  team,
  occupantIds,
  positionId,
  positionName,
  incidentId,
  initialTab = 'team',
  pendingIncident = false,
}) => (
  <Tabs defaultValue={initialTab} className="tw:w-full">
    <TabsList variant="line" className="tw:w-full tw:justify-start">
      <TabsTrigger value="team">Team</TabsTrigger>
      <TabsTrigger value="activation">Activation</TabsTrigger>
      <TabsTrigger value="planning" data-tour="hwcg-roster-planning">
        Planning Phase
      </TabsTrigger>
      <TabsTrigger value="execution" data-tour="hwcg-roster-execution">
        Execution Phase
      </TabsTrigger>
    </TabsList>

    <TabsContent value="team" className="tw:mt-3">
      {team}
    </TabsContent>

    <TabsContent value="activation" className="tw:mt-3 tw:space-y-4">
      {pendingIncident ? (
        <PendingIncidentNote what="The activation checklist and log for this seat" />
      ) : (
        <>
          <PositionActivationChecklist
            positionId={positionId}
            positionName={positionName}
          />
          <PositionActivationLog
            occupantIds={occupantIds}
            positionId={positionId}
            incidentId={incidentId}
          />
        </>
      )}
    </TabsContent>

    <TabsContent value="planning" className="tw:mt-3">
      {pendingIncident ? (
        <PendingIncidentNote what="This seat's planning phase tasks list" />
      ) : (
        <PositionPhaseTasksPanel
          phase="planning"
          userIds={occupantIds}
          positionId={positionId}
          positionName={positionName}
          incidentId={incidentId}
        />
      )}
    </TabsContent>

    <TabsContent value="execution" className="tw:mt-3">
      {pendingIncident ? (
        <PendingIncidentNote what="This seat's execution phase tasks list" />
      ) : (
        <PositionPhaseTasksPanel
          phase="execution"
          userIds={occupantIds}
          positionId={positionId}
          positionName={positionName}
          incidentId={incidentId}
        />
      )}
    </TabsContent>
  </Tabs>
);

export default PositionPhaseTabs;
